import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, Trash2, GripVertical, Image, Video, Type, Heading, Eye, Settings, Users, Briefcase, CheckCircle, BookOpen, ArrowRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { ImagePicker } from "@/components/instructor/ImagePicker";
import { getVideoEmbedUrl, sanitizeVideoEmbedCode, isEmbedCode } from "@/lib/videoEmbed";

type BlockType = "image" | "video" | "text" | "heading" | "featured_programs" | "how_it_works" | "for_entrepreneurs_mentors" | "cta";

interface ContentBlock {
  id: string;
  block_type: BlockType;
  title: string | null;
  content: string | null;
  media_url: string | null;
  sequence_order: number;
  is_active: boolean;
  is_system?: boolean;
}

// Video Block Editor Component with URL and Embed Code support
interface VideoBlockEditorProps {
  block: ContentBlock;
  onUpdate: (updates: Partial<ContentBlock>) => void;
}

function VideoBlockEditor({ block, onUpdate }: VideoBlockEditorProps) {
  // Determine input mode from existing data
  // If content contains iframe, it's embed mode; otherwise URL mode
  const [inputMode, setInputMode] = useState<'url' | 'embed'>(
    block.content && isEmbedCode(block.content) ? 'embed' : 'url'
  );
  const [embedError, setEmbedError] = useState<string | null>(null);
  const [localEmbedCode, setLocalEmbedCode] = useState(block.content || '');
  const [localUrl, setLocalUrl] = useState(block.media_url || '');

  const handleModeChange = (mode: 'url' | 'embed') => {
    setInputMode(mode);
    setEmbedError(null);
    // Clear the other field when switching modes
    if (mode === 'url') {
      onUpdate({ content: null });
      setLocalEmbedCode('');
    } else {
      onUpdate({ media_url: null });
      setLocalUrl('');
    }
  };

  const handleUrlChange = (value: string) => {
    setLocalUrl(value);
    const embedUrl = getVideoEmbedUrl(value);
    onUpdate({ media_url: embedUrl, content: null });
  };

  const handleEmbedCodeChange = (value: string) => {
    setLocalEmbedCode(value);
    
    if (!value.trim()) {
      setEmbedError(null);
      onUpdate({ media_url: null, content: null });
      return;
    }

    const result = sanitizeVideoEmbedCode(value);
    
    if (result.isValid && result.embedUrl) {
      setEmbedError(null);
      // Store embed code in content field, and extracted URL in media_url
      onUpdate({ content: value, media_url: result.embedUrl });
    } else {
      setEmbedError(result.error);
      // Don't update media_url if invalid
      onUpdate({ content: value, media_url: null });
    }
  };

  // Get current embed URL for preview
  const currentEmbedUrl = inputMode === 'embed' 
    ? (block.media_url || null)
    : (block.media_url ? getVideoEmbedUrl(block.media_url) : null);

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <Label className="text-sm font-medium">Video Source</Label>
        <RadioGroup 
          value={inputMode} 
          onValueChange={(v) => handleModeChange(v as 'url' | 'embed')}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="url" id={`url-${block.id}`} />
            <Label htmlFor={`url-${block.id}`} className="cursor-pointer">Video URL</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="embed" id={`embed-${block.id}`} />
            <Label htmlFor={`embed-${block.id}`} className="cursor-pointer">Embed Code</Label>
          </div>
        </RadioGroup>
      </div>

      {inputMode === 'url' ? (
        <div className="space-y-2">
          <Label>Video URL</Label>
          <Input
            value={localUrl}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder="https://vimeo.com/123456789 or https://youtube.com/watch?v=..."
          />
          <p className="text-xs text-muted-foreground">
            Paste any YouTube or Vimeo URL — it will be automatically converted to an embed format.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <Label>Vimeo Embed Code</Label>
          <Textarea
            value={localEmbedCode}
            onChange={(e) => handleEmbedCodeChange(e.target.value)}
            placeholder='<iframe src="https://player.vimeo.com/video/..." width="640" height="360" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>'
            rows={4}
            className={embedError ? 'border-destructive' : ''}
          />
          {embedError ? (
            <div className="flex items-start gap-2 text-destructive text-xs">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{embedError}</span>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Paste the iframe embed code from Vimeo. Only Vimeo and YouTube embeds are allowed for security.
            </p>
          )}
        </div>
      )}

      {/* Video Preview */}
      {currentEmbedUrl && !embedError && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">Preview</Label>
          <div className="aspect-video rounded-lg overflow-hidden border bg-muted">
            <iframe
              src={currentEmbedUrl}
              title="Video preview"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label>Caption (optional)</Label>
        <Input
          value={block.title || ""}
          onChange={(e) => onUpdate({ title: e.target.value })}
          placeholder="Video caption..."
        />
      </div>
    </div>
  );
}

export function HomepageContentManager() {
  const queryClient = useQueryClient();
  const [newBlockType, setNewBlockType] = useState<BlockType>("image");
  const [editingBlock, setEditingBlock] = useState<string | null>(null);

  const { data: blocks = [], isLoading } = useQuery({
    queryKey: ["homepage-content-blocks"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("homepage_content_blocks")
        .select("*")
        .order("sequence_order", { ascending: true });
      if (error) throw error;
      return data as ContentBlock[];
    },
  });

  // Fetch featured curricula for preview
  const { data: featuredCurricula } = useQuery({
    queryKey: ['featured-curricula-preview'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('curricula')
        .select('id, title, description, thumbnail_url')
        .eq('is_published', true)
        .not('thumbnail_url', 'is', null)
        .limit(3);
      
      if (error) throw error;
      return data;
    },
  });

  const createBlock = useMutation({
    mutationFn: async (blockType: BlockType) => {
      const maxOrder = blocks.length > 0 
        ? Math.max(...blocks.map(b => b.sequence_order)) 
        : 0;
      const { error } = await supabase
        .from("homepage_content_blocks")
        .insert({ 
          block_type: blockType, 
          sequence_order: maxOrder + 1,
          title: blockType === "heading" ? "New Heading" : null,
          content: blockType === "text" ? "Enter your content here..." : null,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homepage-content-blocks"] });
      toast.success("Content block added");
    },
    onError: () => toast.error("Failed to add content block"),
  });

  const updateBlock = useMutation({
    mutationFn: async (block: Partial<ContentBlock> & { id: string }) => {
      const { id, ...updates } = block;
      const { error } = await supabase
        .from("homepage_content_blocks")
        .update(updates)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homepage-content-blocks"] });
      toast.success("Block updated");
    },
    onError: () => toast.error("Failed to update block"),
  });

  const deleteBlock = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("homepage_content_blocks")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homepage-content-blocks"] });
      toast.success("Block deleted");
    },
    onError: () => toast.error("Failed to delete block"),
  });

  const moveBlock = useMutation({
    mutationFn: async ({ id, direction }: { id: string; direction: "up" | "down" }) => {
      const currentIndex = blocks.findIndex(b => b.id === id);
      const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      
      if (targetIndex < 0 || targetIndex >= blocks.length) return;
      
      const currentBlock = blocks[currentIndex];
      const targetBlock = blocks[targetIndex];
      
      // Use temporary negative values to avoid unique constraint violations
      const tempOrder = -Date.now();
      
      await supabase
        .from("homepage_content_blocks")
        .update({ sequence_order: tempOrder })
        .eq("id", currentBlock.id);
      
      await supabase
        .from("homepage_content_blocks")
        .update({ sequence_order: currentBlock.sequence_order })
        .eq("id", targetBlock.id);
      
      await supabase
        .from("homepage_content_blocks")
        .update({ sequence_order: targetBlock.sequence_order })
        .eq("id", currentBlock.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homepage-content-blocks"] });
    },
  });

  const getBlockIcon = (type: BlockType) => {
    switch (type) {
      case "image": return <Image className="h-4 w-4" />;
      case "video": return <Video className="h-4 w-4" />;
      case "text": return <Type className="h-4 w-4" />;
      case "heading": return <Heading className="h-4 w-4" />;
      case "featured_programs": return <BookOpen className="h-4 w-4" />;
      case "how_it_works": return <Settings className="h-4 w-4" />;
      case "for_entrepreneurs_mentors": return <Users className="h-4 w-4" />;
      case "cta": return <ArrowRight className="h-4 w-4" />;
    }
  };

  const getBlockLabel = (block: ContentBlock) => {
    if (block.is_system) {
      return block.title || block.block_type.replace(/_/g, ' ');
    }
    return `${block.block_type} Block`;
  };

  const activeBlocks = blocks.filter(b => b.is_active);

  if (isLoading) {
    return <div className="text-muted-foreground">Loading content blocks...</div>;
  }

  return (
    <Tabs defaultValue="manage" className="w-full">
      <TabsList className="mb-4">
        <TabsTrigger value="manage" className="gap-2">
          <Settings className="h-4 w-4" />
          Manage Blocks
        </TabsTrigger>
        <TabsTrigger value="preview" className="gap-2">
          <Eye className="h-4 w-4" />
          Preview Homepage
        </TabsTrigger>
      </TabsList>

      <TabsContent value="manage" className="space-y-6">
        <div className="flex items-center gap-4">
          <Select value={newBlockType} onValueChange={(v) => setNewBlockType(v as BlockType)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Block type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="image">Image</SelectItem>
              <SelectItem value="video">Video</SelectItem>
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="heading">Heading</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => createBlock.mutate(newBlockType)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Block
          </Button>
        </div>

        <div className="space-y-4">
          {blocks.map((block, index) => (
            <Card key={block.id} className={`relative ${!block.is_active ? 'opacity-60' : ''} ${block.is_system ? 'border-primary/30 bg-primary/5' : ''}`}>
              <CardHeader className="flex flex-row items-center justify-between py-3">
                <div className="flex items-center gap-2">
                  <div className="flex flex-col gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => moveBlock.mutate({ id: block.id, direction: "up" })}
                      disabled={index === 0}
                    >
                      ▲
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => moveBlock.mutate({ id: block.id, direction: "down" })}
                      disabled={index === blocks.length - 1}
                    >
                      ▼
                    </Button>
                  </div>
                  <GripVertical className="h-5 w-5 text-muted-foreground" />
                  {getBlockIcon(block.block_type as BlockType)}
                  <CardTitle className="text-base capitalize">
                    #{index + 1} - {getBlockLabel(block)}
                  </CardTitle>
                  {block.is_system && (
                    <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full font-medium">
                      System
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={block.is_active}
                      onCheckedChange={(checked) => updateBlock.mutate({ id: block.id, is_active: checked })}
                    />
                    <Label className="text-sm">Active</Label>
                  </div>
                  {!block.is_system && (
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => deleteBlock.mutate(block.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              {/* Only show content editing for custom blocks, not system blocks */}
              {!block.is_system && (
                <CardContent className="space-y-4">
                  {(block.block_type === "heading" || block.block_type === "text") && (
                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Input
                        value={block.title || ""}
                        onChange={(e) => updateBlock.mutate({ id: block.id, title: e.target.value })}
                        placeholder="Enter title..."
                      />
                    </div>
                  )}

                  {block.block_type === "text" && (
                    <div className="space-y-2">
                      <Label>Content</Label>
                      <Textarea
                        value={block.content || ""}
                        onChange={(e) => updateBlock.mutate({ id: block.id, content: e.target.value })}
                        placeholder="Enter content..."
                        rows={4}
                      />
                    </div>
                  )}

                  {block.block_type === "image" && (
                    <div className="space-y-2">
                      <Label>Image</Label>
                      <ImagePicker
                        value={block.media_url || ""}
                        onChange={(url) => updateBlock.mutate({ id: block.id, media_url: url })}
                      />
                      <div className="space-y-2 mt-2">
                        <Label>Caption (optional)</Label>
                        <Input
                          value={block.title || ""}
                          onChange={(e) => updateBlock.mutate({ id: block.id, title: e.target.value })}
                          placeholder="Image caption..."
                        />
                      </div>
                    </div>
                  )}

                  {block.block_type === "video" && (
                    <VideoBlockEditor 
                      block={block} 
                      onUpdate={(updates) => updateBlock.mutate({ id: block.id, ...updates })}
                    />
                  )}
                </CardContent>
              )}
            </Card>
          ))}

          {blocks.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No content blocks yet. Add one above!
            </div>
          )}
        </div>
      </TabsContent>

      <TabsContent value="preview">
        <div className="border rounded-lg overflow-hidden bg-background">
          <div className="bg-muted/50 p-2 border-b text-center text-sm text-muted-foreground">
            Homepage Preview (content below hero banner)
          </div>
          
          <div className="max-h-[70vh] overflow-y-auto">
            {/* Dynamic Content Blocks Preview */}
            {activeBlocks.length > 0 && (
              <section className="py-12 bg-background border-b">
                <div className="container mx-auto px-4 space-y-10">
                  <div className="text-xs text-center text-muted-foreground mb-4 bg-primary/10 py-1 rounded">
                    📦 Dynamic Content Blocks ({activeBlocks.length} active)
                  </div>
                  {activeBlocks.map((block) => (
                    <div key={block.id} className="max-w-4xl mx-auto">
                      {block.block_type === "heading" && (
                        <h2 className="text-2xl font-bold text-center">{block.title || "Heading"}</h2>
                      )}
                      {block.block_type === "text" && (
                        <div className="space-y-2">
                          {block.title && (
                            <h3 className="text-lg font-semibold text-center">{block.title}</h3>
                          )}
                          <p className="text-muted-foreground text-center leading-relaxed whitespace-pre-wrap text-sm">
                            {block.content || "Text content..."}
                          </p>
                        </div>
                      )}
                      {block.block_type === "image" && (
                        <figure className="space-y-2">
                          {block.media_url ? (
                            <img
                              src={block.media_url}
                              alt={block.title || "Homepage image"}
                              className="w-full rounded-lg object-cover max-h-[300px]"
                            />
                          ) : (
                            <div className="w-full h-[200px] bg-muted rounded-lg flex items-center justify-center">
                              <Image className="h-12 w-12 text-muted-foreground" />
                            </div>
                          )}
                          {block.title && (
                            <figcaption className="text-xs text-muted-foreground text-center">
                              {block.title}
                            </figcaption>
                          )}
                        </figure>
                      )}
                      {block.block_type === "video" && (
                        <figure className="space-y-2">
                          {block.media_url ? (
                            <div className="aspect-video rounded-lg overflow-hidden">
                              <iframe
                                src={getVideoEmbedUrl(block.media_url)}
                                title={block.title || "Homepage video"}
                                className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              />
                            </div>
                          ) : (
                            <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                              <Video className="h-12 w-12 text-muted-foreground" />
                            </div>
                          )}
                          {block.title && (
                            <figcaption className="text-xs text-muted-foreground text-center">
                              {block.title}
                            </figcaption>
                          )}
                        </figure>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Featured Programs Preview */}
            <section className="py-12 bg-muted/50 border-b">
              <div className="container mx-auto px-4">
                <div className="text-xs text-center text-muted-foreground mb-4 bg-accent/10 py-1 rounded">
                  📚 Featured Programs Section (auto-populated from published curricula)
                </div>
                <h2 className="text-2xl font-bold text-center mb-3">Featured Programs</h2>
                <p className="text-muted-foreground text-center mb-8 max-w-2xl mx-auto text-sm">
                  Explore our curated learning paths designed to help you succeed
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  {(featuredCurricula && featuredCurricula.length > 0) ? (
                    featuredCurricula.map((curriculum) => (
                      <Card key={curriculum.id} className="overflow-hidden">
                        {curriculum.thumbnail_url && (
                          <div className="aspect-video overflow-hidden">
                            <img 
                              src={curriculum.thumbnail_url} 
                              alt={curriculum.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <CardHeader className="p-3">
                          <div className="flex items-center gap-2 text-primary mb-1">
                            <BookOpen className="h-3 w-3" />
                            <span className="text-xs font-medium">Curriculum</span>
                          </div>
                          <CardTitle className="text-sm line-clamp-2">{curriculum.title}</CardTitle>
                        </CardHeader>
                      </Card>
                    ))
                  ) : (
                    [1, 2, 3].map((i) => (
                      <Card key={i} className="overflow-hidden">
                        <div className="aspect-video bg-muted flex items-center justify-center">
                          <BookOpen className="h-8 w-8 text-muted-foreground" />
                        </div>
                        <CardHeader className="p-3">
                          <CardTitle className="text-sm text-muted-foreground">Sample Program {i}</CardTitle>
                        </CardHeader>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            </section>

            {/* How It Works Preview */}
            <section className="py-12 bg-secondary border-b">
              <div className="container mx-auto px-4">
                <div className="text-xs text-center text-muted-foreground mb-4 bg-primary/10 py-1 rounded">
                  🔧 How It Works Section (static)
                </div>
                <h2 className="text-2xl font-bold text-center mb-8">How It Works</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {[
                    { num: "1", title: "Create Your Profile", desc: "Sign up and tell us about yourself..." },
                    { num: "2", title: "Get Matched", desc: "Our algorithm suggests mentors..." },
                    { num: "3", title: "EAB Connect", desc: "Schedule focused 15-minute sessions..." },
                  ].map((step) => (
                    <Card key={step.num}>
                      <CardHeader className="p-4">
                        <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center mb-3">
                          <span className="text-lg font-bold text-primary">{step.num}</span>
                        </div>
                        <CardTitle className="text-base">{step.title}</CardTitle>
                        <CardDescription className="text-xs">{step.desc}</CardDescription>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* For Entrepreneurs & Mentors Preview */}
            <section className="py-12 border-b">
              <div className="container mx-auto px-4">
                <div className="text-xs text-center text-muted-foreground mb-4 bg-accent/10 py-1 rounded">
                  👥 For Entrepreneurs & Mentors Section (static)
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="border-2 border-accent/30">
                    <CardHeader className="p-4">
                      <div className="h-10 w-10 bg-accent/10 rounded-full flex items-center justify-center mb-3">
                        <Briefcase className="h-5 w-5 text-accent" />
                      </div>
                      <CardTitle className="text-lg">For Entrepreneurs</CardTitle>
                      <CardDescription className="text-xs">
                        Get focused guidance from mentors...
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <ul className="space-y-2">
                        {["Access to experienced mentors", "Algorithm-matched suggestions"].map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs">
                            <CheckCircle className="h-3 w-3 text-accent flex-shrink-0 mt-0.5" />
                            <span className="text-muted-foreground">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="border-2 border-primary/30">
                    <CardHeader className="p-4">
                      <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center mb-3">
                        <Users className="h-5 w-5 text-primary" />
                      </div>
                      <CardTitle className="text-lg">For Mentors</CardTitle>
                      <CardDescription className="text-xs">
                        Share your expertise and help shape...
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <ul className="space-y-2">
                        {["Give back to community", "Discover opportunities"].map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs">
                            <CheckCircle className="h-3 w-3 text-primary flex-shrink-0 mt-0.5" />
                            <span className="text-muted-foreground">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </section>

            {/* CTA Preview */}
            <section className="py-12 bg-primary text-primary-foreground">
              <div className="container mx-auto px-4 text-center">
                <div className="text-xs text-center text-primary-foreground/70 mb-4 bg-primary-foreground/10 py-1 rounded">
                  🎯 Call to Action Section (static)
                </div>
                <h2 className="text-xl font-bold mb-3">Ready to Start Your Journey?</h2>
                <p className="text-sm mb-6 opacity-90 max-w-xl mx-auto">
                  Join our community of entrepreneurs and mentors...
                </p>
                <Button size="sm" className="bg-accent hover:bg-accent/90 text-accent-foreground">
                  Get Started Today
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </section>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
}
